import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { formatCurrency, formatDate } from "./api";
import { drawPdfBrand } from "./brand";

const NAVY = [29, 78, 216]; // #1D4EB8
const SLATE_900 = [15, 23, 42];
const SLATE_500 = [100, 116, 139];
const SLATE_200 = [226, 232, 240];
const ROSE = [244, 63, 94];
const EMERALD = [16, 185, 129];
const WHITE = [255, 255, 255];

async function captureGantt(selector = '[data-testid="gantt-chart"]') {
  const el = document.querySelector(selector);
  if (!el) return null;
  try {
    const canvas = await html2canvas(el, {
      backgroundColor: "#0b1120",
      scale: 2,
      logging: false,
      useCORS: true,
    });
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
}

export async function exportProjectPDF({ project, plan, tasks, roles, risk, programName, risks = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Header bar
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Compass PM", marginX, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("by Uplift Communications Corporation", marginX, 44);
  doc.setFontSize(9);
  doc.text(`Status pack generated ${new Date().toLocaleString()}`, pageW - marginX, 32, { align: "right" });
  drawPdfBrand(doc, pageW, marginX, `Status pack generated ${new Date().toLocaleString()}`);

  let y = 80;
  // Project title
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(project.name || "Project", marginX, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_500);
  if (project.description) {
    const descLines = doc.splitTextToSize(project.description, pageW - marginX * 2);
    doc.text(descLines, marginX, y);
    y += descLines.length * 12;
  }
  const meta = [];
  if (programName) meta.push(`Program: ${programName}`);
  if (project.stakeholders) meta.push(`Stakeholders: ${project.stakeholders}`);
  if (project.stated_due_date) meta.push(`Due: ${formatDate(project.stated_due_date)}`);
  if (project.stated_budget) meta.push(`Budget: ${formatCurrency(project.stated_budget)}`);
  if (meta.length) {
    doc.text(meta.join("   •   "), marginX, y + 10);
    y += 22;
  }

  y += 10;

  // Summary cards (3 x 2 grid)
  const estVar = (plan.estimatedCost || 0) - Number(project.stated_budget || 0);
  const schedDays = project.stated_due_date && plan.plannedDelivery
    ? Math.round((plan.plannedDelivery - new Date(project.stated_due_date)) / 86400000) : null;
  const cards = [
    { l: "Calculated Delivery", v: plan.plannedDelivery ? formatDate(plan.plannedDelivery) : "—" },
    { l: "Estimated Cost", v: formatCurrency(plan.estimatedCost) },
    { l: "Actual Delivery", v: plan.actualDelivery ? formatDate(plan.actualDelivery) : "—" },
    { l: "Actual Cost", v: formatCurrency(plan.actualCost) },
    { l: "Schedule Variance", v: schedDays === null ? "—" : schedDays > 0 ? `+${schedDays}d late` : `${Math.abs(schedDays)}d ahead`, bad: schedDays > 0 },
    { l: "Budget Variance", v: formatCurrency(estVar), bad: estVar > 0 },
  ];
  const cardW = (pageW - marginX * 2 - 20) / 3;
  const cardH = 54;
  cards.forEach((c, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const cx = marginX + col * (cardW + 10);
    const cy = y + row * (cardH + 10);
    doc.setDrawColor(...SLATE_200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(cx, cy, cardW, cardH, 6, 6, "FD");
    doc.setTextColor(...SLATE_500);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(c.l.toUpperCase(), cx + 10, cy + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...(c.bad ? ROSE : SLATE_900));
    doc.text(String(c.v), cx + 10, cy + 38);
  });
  y += cardH * 2 + 20;

  // Risk score band
  if (risk) {
    const riskColor = risk.tier === "high" ? ROSE : risk.tier === "medium" ? [245, 158, 11] : EMERALD;
    doc.setDrawColor(...SLATE_200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(marginX, y, pageW - marginX * 2, 46, 6, 6, "FD");
    doc.setTextColor(...SLATE_500);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("RISK SCORE", marginX + 12, y + 16);
    doc.setFontSize(22);
    doc.setTextColor(...riskColor);
    doc.text(`${risk.score}`, marginX + 12, y + 36);
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    doc.text(`/100  —  ${risk.tier.toUpperCase()}`, marginX + 60, y + 36);
    // Factor breakdown
    const factors = Object.entries(risk.factors || {});
    factors.forEach(([k, v], i) => {
      const fx = marginX + 180 + i * 90;
      doc.setFontSize(7);
      doc.setTextColor(...SLATE_500);
      doc.text(k.replace(/_/g, " ").toUpperCase(), fx, y + 16);
      doc.setFontSize(12);
      doc.setTextColor(...SLATE_900);
      doc.text(`${v}`, fx, y + 36);
    });
    y += 56;
  }

  // Gantt image
  try {
    const img = await captureGantt();
    if (img) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...SLATE_900);
      doc.text("Timeline", marginX, y);
      y += 10;
      const w = pageW - marginX * 2;
      const h = (w / 1600) * 680;  // approx aspect
      if (y + h > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage(); y = 60;
      }
      doc.addImage(img, "PNG", marginX, y, w, h, undefined, "FAST");
      y += h + 20;
    }
  } catch (e) { console.warn("Gantt capture skipped — proceeding without timeline in PDF", e); }

  // Task table on new page
  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...SLATE_900);
  doc.text("Task plan", marginX, 60);

  const unitHoursMap = { Minutes: 1 / 60, Hours: 1, Days: 8, Weeks: 40 };
  const uh = unitHoursMap[project.time_unit || "Days"] || 8;
  const rateById = new Map(roles.map((r) => [r.id, Number(r.rate || 0)]));
  const rows = tasks.map((t) => {
    const rate = rateById.get(t.role_id) || 0;
    const estCost = Number(t.estimated_time || 0) * uh * rate;
    const planTask = plan.tasks.find((p) => p.id === t.id);
    const crit = planTask?._critical ? " ★" : "";
    return [
      `${t.task_number}${crit}`,
      t.name || "",
      t.role_name || "—",
      t.resource_name || "—",
      t.order ?? "",
      t.order_type || "",
      Number(t.estimated_time || 0),
      formatCurrency(estCost),
      t.actual_time ?? "",
      t.actual_cost != null ? formatCurrency(t.actual_cost) : "",
      t.status || "Not Started",
    ];
  });
  autoTable(doc, {
    startY: 70,
    head: [["#", "Task", "Role", "Resource", "Order", "Type", "Est", "Est Cost", "Act", "Act Cost", "Status"]],
    body: rows,
    styles: { fontSize: 8, cellPadding: 4, textColor: SLATE_900 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 110 }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" } },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0 && typeof data.cell.raw === "string" && data.cell.raw.includes("★")) {
        data.cell.styles.textColor = ROSE;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  // Risk register — only open & mitigated (closed risks are history)
  const openRisks = (risks || []).filter((r) => (r.status || "Open") !== "Closed");
  if (openRisks.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...SLATE_900);
    doc.text("Risk register", marginX, 60);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    doc.text(
      `${openRisks.length} open / mitigated risk${openRisks.length === 1 ? "" : "s"} tracked for this project.`,
      marginX,
      76,
    );
    const taskByNum = new Map((tasks || []).map((t) => [Number(t.task_number), t.name || ""]));
    const riskRows = openRisks.map((r) => {
      const taskLabel = r.task_number != null && taskByNum.has(Number(r.task_number))
        ? `#${r.task_number} ${taskByNum.get(Number(r.task_number))}`
        : "—";
      return [
        taskLabel,
        r.description || "",
        r.likelihood || "Medium",
        r.impact || "Medium",
        r.mitigation || "",
        r.owner || "—",
        r.status || "Open",
      ];
    });
    autoTable(doc, {
      startY: 88,
      head: [["Linked task", "Risk", "Likelihood", "Impact", "Mitigation", "Owner", "Status"]],
      body: riskRows,
      styles: { fontSize: 8, cellPadding: 4, textColor: SLATE_900, valign: "top" },
      headStyles: { fillColor: ROSE, textColor: WHITE, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [254, 242, 242] },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 140 },
        2: { cellWidth: 50, halign: "center" },
        3: { cellWidth: 50, halign: "center" },
        4: { cellWidth: 140 },
        5: { cellWidth: 60 },
        6: { cellWidth: 50, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const col = data.column.index;
        const raw = String(data.cell.raw || "");
        if (col === 2 || col === 3) {
          if (raw === "High") { data.cell.styles.textColor = ROSE; data.cell.styles.fontStyle = "bold"; }
          else if (raw === "Low") { data.cell.styles.textColor = EMERALD; }
        }
        if (col === 6) {
          if (raw === "Open") { data.cell.styles.textColor = ROSE; data.cell.styles.fontStyle = "bold"; }
          else if (raw === "Mitigated") { data.cell.styles.textColor = [217, 119, 6]; data.cell.styles.fontStyle = "bold"; }
        }
      },
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...SLATE_500);
    doc.text(
      `Compass PM status pack  •  ${project.name || "Project"}  •  Page ${i} of ${pageCount}`,
      marginX,
      doc.internal.pageSize.getHeight() - 24,
    );
  }

  doc.save(`${(project.name || "project").replace(/[^a-z0-9]+/gi, "_")}_status.pdf`);
}


// ---------------------------- Cost estimate PDF ----------------------------
// Standalone, on-demand "cost estimate" PDF: one line per task with hours and
// estimated cost, plus a grand total. Returns the jsPDF document so the caller
// can .save() for a local download AND .output('blob') to archive on the server
// — mirrors how auditors expect a signed, dated artifact.
export async function buildCostEstimatePDF({ project, tasks, roles, timeUnit = "Days", authorName = "" }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Compass PM", marginX, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("by Uplift Communications Corporation", marginX, 44);
  doc.setFontSize(9);
  doc.text(`Cost estimate generated ${new Date().toLocaleString()}`, pageW - marginX, 32, { align: "right" });
  drawPdfBrand(doc, pageW, marginX, `Cost estimate generated ${new Date().toLocaleString()}`);

  let y = 80;
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(`${project.name || "Project"} — cost estimate`, marginX, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_500);
  const meta = [];
  if (authorName) meta.push(`Prepared by: ${authorName}`);
  if (project.stated_due_date) meta.push(`Target: ${formatDate(project.stated_due_date)}`);
  if (project.stated_budget) meta.push(`Stated budget: ${formatCurrency(project.stated_budget)}`);
  meta.push(`Time unit: ${timeUnit}`);
  doc.text(meta.join("   •   "), marginX, y);
  y += 20;

  // Rows
  const stdMap = { Minutes: 1 / 60, Hours: 1, Days: 8, Weeks: 40 };
  const extMap = { Minutes: 1 / 60, Hours: 1, Days: 24, Weeks: 168 };
  const rateById = new Map(roles.map((r) => [r.id, Number(r.rate || 0)]));
  const rows = [];
  let totalHours = 0;
  let totalCost = 0;
  for (const t of tasks) {
    const estTime = Number(t.estimated_time || 0);
    const hoursPerUnit = (t.extended_hours ? extMap : stdMap)[timeUnit] || stdMap.Days;
    const rate = rateById.get(t.role_id) || 0;
    const hours = estTime * hoursPerUnit;
    const estCost = hours * rate;
    totalHours += hours;
    totalCost += estCost;
    rows.push([
      t.task_number,
      t.name || "",
      t.role_name || "—",
      estTime,
      t.extended_hours ? "24h/d" : "",
      hours.toFixed(2),
      formatCurrency(rate) + "/h",
      formatCurrency(estCost),
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [["#", "Task", "Role", `Est ${timeUnit}`, "Ext", "Hours", "Rate", "Est Cost"]],
    body: rows,
    foot: [[
      "", { content: "TOTAL", styles: { fontStyle: "bold", halign: "right" } },
      "", "", "",
      { content: totalHours.toFixed(2), styles: { fontStyle: "bold" } },
      "",
      { content: formatCurrency(totalCost), styles: { fontStyle: "bold", textColor: NAVY } },
    ]],
    styles: { fontSize: 9, cellPadding: 5, textColor: SLATE_900 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
    footStyles: { fillColor: [241, 245, 249], textColor: SLATE_900 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 28, halign: "right" },
      1: { cellWidth: 180 },
      2: { cellWidth: 80 },
      3: { cellWidth: 60, halign: "right" },
      4: { cellWidth: 40, halign: "center" },
      5: { cellWidth: 54, halign: "right" },
      6: { cellWidth: 70, halign: "right" },
      7: { cellWidth: 80, halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 4 && data.cell.raw === "24h/d") {
        data.cell.styles.textColor = NAVY;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  // Footer with page numbers + grand total summary
  const summaryY = (doc.lastAutoTable?.finalY || y) + 28;
  const grandBudget = Number(project.stated_budget || 0);
  const variance = totalCost - grandBudget;
  const summary = [
    { l: "Total estimated hours", v: `${totalHours.toFixed(2)} h` },
    { l: "Total estimated cost", v: formatCurrency(totalCost) },
    { l: "Stated budget", v: grandBudget ? formatCurrency(grandBudget) : "—" },
    { l: "Variance vs budget", v: grandBudget ? formatCurrency(variance) : "—", bad: variance > 0 && grandBudget > 0 },
  ];
  const cardW = (pageW - marginX * 2 - 30) / 4;
  const cardH = 54;
  if (summaryY + cardH < doc.internal.pageSize.getHeight() - 60) {
    summary.forEach((c, i) => {
      const cx = marginX + i * (cardW + 10);
      doc.setDrawColor(...SLATE_200);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(cx, summaryY, cardW, cardH, 6, 6, "FD");
      doc.setTextColor(...SLATE_500);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text(c.l.toUpperCase(), cx + 10, summaryY + 16);
      doc.setFontSize(14);
      doc.setTextColor(...(c.bad ? ROSE : SLATE_900));
      doc.text(String(c.v), cx + 10, summaryY + 38);
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...SLATE_500);
    doc.text(
      `Compass PM cost estimate  •  ${project.name || "Project"}  •  Page ${i} of ${pageCount}`,
      marginX,
      doc.internal.pageSize.getHeight() - 24,
    );
  }

  return { doc, totals: { total_hours: totalHours, total_estimated_cost: totalCost, task_count: tasks.length, time_unit: timeUnit } };
}
